/* eslint-disable @typescript-eslint/no-explicit-any */
import { getDataValue, setDataValue } from '@juvare/json-data-util';
import React from 'react';
import { getErrorMessage, mapToArray, mapToBoolean, mapToBooleanOrUndefined, mapToNumber, mapToNumberOrUndefined, mapToString, mapToStringOrUndefined, } from '../Utils';
import { unknownWidget, walkWidgetPropsTree } from '../Widget';
export function useRootWidget(rootParams, widgets, editMode, config, data, onDataChange) {
    const [rootAndKey, setRootAndKey] = React.useState({ key: 0 });
    const [, setRenderFlag] = React.useState();
    const render = React.useCallback(() => {
        setRenderFlag({});
    }, []);
    const params = React.useRef({
        rootParams: rootParams,
        widgets: widgets,
        editMode: editMode,
        config: config,
        data: data,
        onDataChange: onDataChange,
        render: render,
    });
    React.useEffect(() => {
        if (params.current.rootParams !== rootParams) {
            params.current.rootParams = rootParams;
        }
        if (params.current.widgets !== widgets) {
            params.current.widgets = widgets;
        }
        if (params.current.editMode !== editMode) {
            params.current.editMode = editMode;
        }
        if (params.current.config !== config) {
            params.current.config = config;
        }
        if (params.current.data !== data) {
            params.current.data = data;
            params.current.cacheData = undefined;
        }
        const [root, cacheData] = createRootWidget(params.current);
        params.current.cacheData = cacheData;
        setRootAndKey((prevValue) => ({ root, key: prevValue.key + 1 }));
    }, [rootParams, widgets, config, editMode, data]);
    React.useEffect(() => {
        params.current.onDataChange = onDataChange;
    }, [onDataChange]);
    return [rootAndKey.root, rootAndKey.key];
}
function createRootWidget(params) {
    var _a;
    const cache = {
        data: (_a = params.cacheData) !== null && _a !== void 0 ? _a : JSON.parse(JSON.stringify(params.data)),
        nextId: 1,
        widgetMap: Object.fromEntries(params.widgets.map((widget) => [widget.type, widget])),
        renderOnAnyError: [],
        errorCount: 0,
        rootWidgetProps: undefined,
        skipValidateUpdateDuringInit: false,
    };
    const markForRender = (props) => {
        ++props.renderCounter;
        let parent = props.parentProps;
        while (parent) {
            ++parent.renderCounter;
            parent = parent.parentProps;
        }
    };
    const updateIndexAndPathForDynamicRows = (props) => {
        updateIndexAndPathForDynamicRowsRecursive(props);
    };
    const updateIndexAndPathForDynamicRowsRecursive = (props) => {
        markForRender(props);
        if (props.childProps) {
            for (const childProps of props.childProps) {
                const newDataPath = joinPaths(props.dataPath, childProps.config.key);
                if (childProps.dataPath !== newDataPath) {
                    markForRender(childProps);
                    childProps.dataPath = newDataPath;
                    updateIndexAndPathForDynamicRowsRecursive(childProps);
                }
            }
        }
        if (props.childDynamicRows) {
            for (let i = 0; i < props.childDynamicRows.length; i++) {
                const row = props.childDynamicRows[i];
                if (row.index !== i) {
                    row.renderCounter++;
                    row.index = i;
                    for (const childProps of row.childProps) {
                        const newDataPath = joinPaths(joinPaths(props.dataPath, '#' + i), childProps.config.key);
                        if (childProps.dataPath !== newDataPath) {
                            childProps.dataPath = newDataPath;
                            updateIndexAndPathForDynamicRowsRecursive(childProps);
                        }
                    }
                }
            }
        }
    };
    const setError = (props, error) => {
        if (error === '') {
            error = undefined; // empty string for an error message is a bad idea, the spec says to convert it to undefined
        }
        if (props.error !== error) {
            const wasError = !!props.error;
            const isError = !!error;
            props.error = error;
            markForRender(props);
            cache.renderOnAnyError.forEach((props) => props.update());
            if (!wasError && isError) {
                ++cache.errorCount;
            }
            else if (wasError && !isError) {
                --cache.errorCount;
            }
        }
    };
    const create = (config, parentProps, parentDynamicRow) => {
        var _a, _b;
        const id = parentProps ? joinPaths(joinPaths(parentProps.id, parentDynamicRow === null || parentDynamicRow === void 0 ? void 0 : parentDynamicRow.id), 'w' + cache.nextId++) : 'w' + cache.nextId++;
        const dataPath = parentProps
            ? joinPaths(joinPaths(parentProps.dataPath, parentDynamicRow ? '#' + parentDynamicRow.index : ''), config.key)
            : ((_a = config.key) !== null && _a !== void 0 ? _a : '');
        const widget = (_b = cache.widgetMap[config.type]) !== null && _b !== void 0 ? _b : unknownWidget;
        let childProps = undefined;
        let childDynamicRows = undefined;
        if (widget.childConfigs) {
            if (!widget.childDynamicRows) {
                childProps = [];
            }
            else {
                childDynamicRows = [];
            }
        }
        const getData = (mapper) => {
            if (!props.editMode) {
                const value = getDataValue(cache.data, props.dataPath);
                return mapper ? mapper(value) : value;
            }
            else {
                return mapper ? mapper(undefined) : undefined;
            }
        };
        const setData = (value, mapper) => {
            if (!props.editMode) {
                setDataValue(cache.data, props.dataPath, mapper ? mapper(value) : value);
                markForRender(props);
            }
        };
        const createDataRow = (dataRowIndex) => {
            const rowId = 'r' + cache.nextId++;
            const dynamicRow = {
                id: joinPaths(id, rowId),
                index: dataRowIndex,
                childProps: [],
                renderCounter: 0,
            };
            if (props.widget.childConfigs) {
                props.widget
                    .childConfigs(config, props)
                    .forEach((childConfig) => dynamicRow.childProps.push(create(childConfig, props, dynamicRow)));
            }
            return dynamicRow;
        };
        const insertDataRow = (start) => {
            if (!props.editMode && props.widget.childConfigs && props.childDynamicRows) {
                if (start == null) {
                    start = props.childDynamicRows.length;
                }
                start = parseInt(start); // make sure that start param work exactly like splice start param
                if (start < 0) {
                    start = props.childDynamicRows.length - start;
                    if (Number.NEGATIVE_INFINITY === start) {
                        start = 0;
                    }
                }
                else if (start > props.childDynamicRows.length) {
                    start = props.childDynamicRows.length;
                }
                const row = createDataRow(start);
                props.childDynamicRows.splice(start, 0, row);
                updateIndexAndPathForDynamicRows(props);
                const values = getData(mapToArray);
                values.splice(start, 0, undefined);
                cache.skipValidateUpdateDuringInit = true;
                row.childProps.forEach(init);
                cache.skipValidateUpdateDuringInit = false;
                setData(values);
            }
        };
        const deleteDataRow = (start) => {
            if (!props.editMode && props.widget.childConfigs && props.childDynamicRows) {
                const [deletedRow] = props.childDynamicRows.splice(start, 1);
                for (const deletedRowChildProps of deletedRow.childProps) {
                    walkWidgetPropsTree(deletedRowChildProps, (p) => {
                        setError(p, undefined);
                    });
                }
                const values = getData(mapToArray);
                values.splice(start, 1);
                updateIndexAndPathForDynamicRows(props);
                setData(values);
            }
        };
        const moveDataRow = (fromIndex, toIndex) => {
            if (!props.editMode && props.widget.childConfigs && props.childDynamicRows) {
                const length = props.childDynamicRows.length;
                if (fromIndex >= 0 && fromIndex < length && toIndex >= 0 && toIndex < length && fromIndex !== toIndex) {
                    const tempRow = props.childDynamicRows[fromIndex];
                    props.childDynamicRows[fromIndex] = props.childDynamicRows[toIndex];
                    props.childDynamicRows[toIndex] = tempRow;
                    const values = getData(mapToArray);
                    const tempValue = values[fromIndex];
                    values[fromIndex] = values[toIndex];
                    values[toIndex] = tempValue;
                    updateIndexAndPathForDynamicRows(props);
                    setData(values);
                }
            }
        };
        const recreateChildProps = () => {
            if (childProps) {
                for (const childProp of childProps) {
                    walkWidgetPropsTree(childProp, (p) => {
                        setError(p, undefined);
                    });
                }
                childProps.splice(0);
                if (widget.childConfigs) {
                    for (const childConfig of widget.childConfigs(config, props)) {
                        childProps.push(create(childConfig, props));
                    }
                }
                for (const childProp of childProps) {
                    init(childProp);
                }
                markForRender(props);
            }
        };
        const props = {
            id: id,
            widget: widget,
            widgets: params.widgets,
            editMode: params.editMode,
            config: config,
            dataPath: dataPath,
            nestingLevel: parentProps ? parentProps.nestingLevel + 1 : 0,
            childProps: childProps,
            childDynamicRows: childDynamicRows,
            parentProps: parentProps,
            parentDynamicRow: parentDynamicRow,
            getData: getData,
            setData: setData,
            getDataString: () => getData(mapToString),
            setDataString: (value) => setData(value, mapToString),
            getDataStringOrUndefined: () => getData(mapToStringOrUndefined),
            setDataStringOrUndefined: (value) => setData(value, mapToStringOrUndefined),
            getDataNumber: () => getData(mapToNumber),
            setDataNumber: (value) => setData(value, mapToNumber),
            getDataNumberOrUndefined: () => getData(mapToNumberOrUndefined),
            setDataNumberOrUndefined: (value) => setData(value, mapToNumberOrUndefined),
            getDataBoolean: () => getData(mapToBoolean),
            setDataBoolean: (value) => setData(value, mapToBoolean),
            getDataBooleanOrUndefined: () => getData(mapToBooleanOrUndefined),
            setDataBooleanOrUndefined: (value) => setData(value, mapToBooleanOrUndefined),
            getDataArray: () => getData(mapToArray),
            insertDataRow: (start) => insertDataRow(start),
            deleteDataRow: (start) => deleteDataRow(start),
            moveDataRow: (from, to) => moveDataRow(from, to),
            recreateChildProps: recreateChildProps,
            internalData: {},
            renderCounter: 0,
            update: () => markForRender(props),
            render: () => {
                validate();
                update();
                return render();
            },
            rootParams: params.rootParams,
            rootConfig: params.config,
            rootData: cache.data,
            rootWidgetProps: cache.rootWidgetProps,
            rootUpdateAll: () => {
                walkWidgetPropsTree(rootWidgetProps, (props) => props.update());
            },
            getRootErrorCount: () => {
                return cache.errorCount;
            },
        };
        if (widget.renderOnAnyError) {
            cache.renderOnAnyError.push(props);
        }
        if (!parentProps) {
            props.rootWidgetProps = props;
            cache.rootWidgetProps = props;
        }
        if (widget.childConfigs) {
            if (childProps) {
                for (const childConfig of widget.childConfigs(config, props)) {
                    childProps.push(create(childConfig, props));
                }
            }
            if (childDynamicRows) {
                if (props.editMode) {
                    childDynamicRows.push(createDataRow(0));
                }
                else {
                    const values = getData(mapToArray);
                    for (let i = 0; i < values.length; i++) {
                        childDynamicRows.push(createDataRow(i));
                    }
                }
            }
        }
        return props;
    };
    const init = (props) => {
        if (props.widget.init) {
            // this call can and will modify data, including dynamic rows
            try {
                props.widget.init(props);
            }
            catch (e) {
                console.error('Widget init threw an error, this should not happen', e);
                setError(props, getErrorMessage(e));
            }
        }
        if (props.childProps) {
            // parents are initialized first, then children
            props.childProps.forEach(init);
        }
        if (props.childDynamicRows) {
            // this array could have been initialized during above init call
            for (const row of props.childDynamicRows) {
                row.childProps.forEach(init);
            }
        }
        if (props.widget.load) {
            // this call can and will modify data, including dynamic rows
            try {
                props.widget.load(props);
            }
            catch (e) {
                console.error('Widget load threw an error, this should not happen', e);
                setError(props, getErrorMessage(e));
            }
        }
    };
    const validateRecursive = (props, parentHidden) => {
        if (props.widget.validate) {
            if (parentHidden || props.hidden) {
                setError(props, undefined);
            }
            else {
                try {
                    setError(props, props.widget.validate(props));
                }
                catch (e) {
                    console.error('Widget validate threw an error, this should not happen', e);
                    setError(props, getErrorMessage(e));
                }
            }
        }
        if (props.childProps) {
            props.childProps.forEach((p) => validateRecursive(p, parentHidden || props.hidden));
        }
        if (props.childDynamicRows) {
            for (const row of props.childDynamicRows) {
                row.childProps.forEach((p) => validateRecursive(p, parentHidden || props.hidden));
            }
        }
    };
    const validate = () => {
        if (cache.skipValidateUpdateDuringInit) {
            return;
        }
        try {
            validateRecursive(rootWidgetProps, rootWidgetProps.hidden);
        }
        catch (e) {
            console.error('Caught error during widget validate', e);
        }
    };
    const updateRecursive = (props, result) => {
        if (props.widget.update) {
            try {
                // eslint-disable-next-line testing-library/render-result-naming-convention
                const changed = props.widget.update(props);
                if (changed) {
                    result.changed = true;
                    markForRender(props);
                }
            }
            catch (e) {
                console.error('Widget update threw an error, this should not happen', e);
                setError(props, getErrorMessage(e));
            }
        }
        if (props.childProps) {
            for (const childProps of props.childProps) {
                updateRecursive(childProps, result);
            }
        }
        if (props.childDynamicRows) {
            for (const row of props.childDynamicRows) {
                for (const childProps of row.childProps) {
                    updateRecursive(childProps, result);
                }
            }
        }
    };
    const update = () => {
        if (cache.skipValidateUpdateDuringInit) {
            return;
        }
        const result = { changed: true };
        let updateCounter = 0;
        try {
            while (result.changed) {
                if (updateCounter >= 100) {
                    console.warn('Widget update loop detected, stopping after ' + updateCounter + ' updates');
                    break;
                }
                result.changed = false;
                updateRecursive(rootWidgetProps, result);
                if (result.changed) {
                    validate();
                }
                updateCounter++;
            }
        }
        catch (e) {
            console.error('Caught error during widget update', e);
        }
    };
    const render = () => {
        params.render();
        const widgetData = { data: cache.data, valid: cache.errorCount === 0 };
        if (params.onDataChange) {
            params.onDataChange(widgetData);
        }
        return widgetData;
    };
    const rootWidgetProps = create(params.config);
    cache.skipValidateUpdateDuringInit = true;
    init(rootWidgetProps);
    cache.skipValidateUpdateDuringInit = false;
    validate();
    update();
    render();
    return [rootWidgetProps, cache.data];
}
function joinPaths(parentDataPath, childDataPath) {
    const parent = !!parentDataPath;
    const child = !!childDataPath;
    if (parent && child) {
        return parentDataPath + '.' + childDataPath;
    }
    else if (parent) {
        return parentDataPath;
    }
    else if (child) {
        return childDataPath;
    }
    else {
        return '';
    }
}
